import React from "react";
import { CheckCircle2, Clock, Ticket, CreditCard } from "lucide-react";
import { CompanyReservationsData } from "@/modules/eventRegistration/features/EventDirectory/api/fetchCompanyReservations/types";
import {
  formatDateRangeInTimeZone,
  formatTimeRangeInTimeZone,
} from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";

interface CompanyReservationsProps {
  data: CompanyReservationsData;
  loading: boolean;
}

const CompanyReservations: React.FC<CompanyReservationsProps> = ({
  data,
  loading,
}) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div
            className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 animate-pulse"
            key={i}
          >
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/3 mb-3" />
            <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2 mb-4" />
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (data.sessions.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-sm text-gray-400 dark:text-gray-500">
          No reservations for your company on this event yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {data.sessions.map((session) => {
        const dateRange = formatDateRangeInTimeZone(
          session.startDate,
          session.endDate,
          session.timezoneIdentifier,
        );
        const timeRange = formatTimeRangeInTimeZone(
          session.startDate,
          session.endDate,
          session.timezoneIdentifier,
        );
        const timeDisplay = session.timezoneShortName
          ? `${timeRange} (${session.timezoneShortName})`
          : timeRange;

        return (
          <div key={session.sessionUuid}>
            {/* Session header */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {session.sessionName}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {dateRange} · {timeDisplay}
              </p>
            </div>

            {/* Enrollments */}
            {session.enrollments.length > 0 && (
              <div className="rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-700/50 mb-3">
                {session.enrollments.map((enrollment, idx) => (
                  <div className="px-4 py-3 flex items-start gap-3" key={idx}>
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {enrollment.firstName} {enrollment.lastName}
                        </span>
                        {enrollment.usedVoucher ? (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <Ticket className="h-3 w-3" />
                            Voucher
                          </span>
                        ) : enrollment.seatPrice ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                            <CreditCard className="h-3 w-3" />
                            ${parseFloat(enrollment.seatPrice).toFixed(2)}
                          </span>
                        ) : null}
                      </div>
                      {enrollment.email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {enrollment.email}
                        </p>
                      )}
                      {enrollment.confirmationNumber && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          Confirmation: {enrollment.confirmationNumber}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Waitlist entries */}
            {session.waitlistEntries.length > 0 && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 divide-y divide-amber-100 dark:divide-amber-900/30">
                {session.waitlistEntries.map((entry, idx) => (
                  <div className="px-4 py-3 flex items-start gap-3" key={idx}>
                    <Clock className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {entry.firstName} {entry.lastName}
                        </span>
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          Waitlist #{entry.waitlistPosition}
                        </span>
                      </div>
                      {entry.email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {entry.email}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompanyReservations;
